import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";

/**
 * Global keyboard shortcut handler (REQ-UI-020).
 *
 * Implementuje keyboard interaction matrix:
 *   - `Esc`     — zavřít topmost overlay (delegováno modal components;
 *                  zde dispatchne 'ui:escape' window event)
 *   - `/`       — focus on first `[data-search-input]`
 *   - `?`       — toggle help overlay (`ui:helpOverlay` event)
 *   - `g s`     — jump to /admin/sync
 *   - `g b`     — jump to /admin/budgets
 *   - `g p`     — jump to /admin/distribution (payments)
 *   - `g a`     — jump to /admin/anomalies
 *   - `g h`     — jump to /admin/dashboard (home)
 *
 * Chord `g X` reset after 1500 ms; ignorováno v input/textarea/contenteditable.
 */
type Chord = null | "g";

const JUMP_TARGETS: Record<string, string> = {
  s: "/admin/sync",
  b: "/admin/budgets",
  p: "/admin/distribution",
  a: "/admin/anomalies",
  h: "/admin/dashboard",
};

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(): void {
  const navigate = useNavigate();
  const [, setChord] = useState<Chord>(null);

  useEffect(() => {
    let pending: Chord = null;
    let resetTimer: ReturnType<typeof setTimeout> | null = null;

    const clearChord = () => {
      pending = null;
      setChord(null);
      if (resetTimer) {
        clearTimeout(resetTimer);
        resetTimer = null;
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "Escape") {
        window.dispatchEvent(new CustomEvent("ui:escape"));
        clearChord();
        return;
      }
      if (e.key === "/") {
        const el = document.querySelector<HTMLInputElement>("[data-search-input]");
        if (el) {
          e.preventDefault();
          el.focus();
        }
        return;
      }
      if (e.key === "?") {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("ui:helpOverlay"));
        return;
      }
      if (e.key === "g") {
        pending = "g";
        setChord("g");
        if (resetTimer) clearTimeout(resetTimer);
        resetTimer = setTimeout(clearChord, 1500);
        return;
      }
      if (pending === "g") {
        const target = JUMP_TARGETS[e.key];
        if (target) {
          e.preventDefault();
          void Promise.resolve(navigate({ to: target })).catch(() => undefined);
        }
        clearChord();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, [navigate]);
}
